import { fetchQuery, graphql } from "relay-runtime";
import { Network, signers } from "rujira.js";
import { configureUtxoFetcher } from "rujira.ui";
import { Id } from "../node";
import { utxoQuery } from "./__generated__/utxoQuery.graphql";
import { environment } from "./relay";

const query = graphql`
  query utxoQuery($id: ID!, $after: String) {
    node(id: $id) {
      ... on Account {
        balancesV2(first: 100, after: $after, addresses: []) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              accounts {
                address
                chain
                asset {
                  type
                }
                utxos {
                  oIndex
                  oTxHash
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

const fetchUtxos = async (
  network: Network,
  address: string
): Promise<signers.utxo.Utxo[]> => {
  let after: string | null | undefined;

  do {
    const result = await fetchQuery<utxoQuery>(
      environment,
      query,
      { id: Id.account({ address }), after },
      { fetchPolicy: "network-only" }
    ).toPromise();
    const balances = result?.node?.balancesV2;

    if (!balances) {
      throw new Error(`Unable to load ${network} transaction inputs`);
    }

    const account = balances.edges
      ?.flatMap((edge) => edge?.node?.accounts || [])
      .find(
        (item) =>
          item.address === address &&
          item.chain === network &&
          item.asset.type === "LAYER_1"
      );

    if (account) {
      if (!account.utxos) {
        throw new Error(`Unable to load ${network} transaction inputs`);
      }

      return account.utxos.map((utxo) => ({
        index: utxo.oIndex,
        hash: utxo.oTxHash,
        value: utxo.value,
      }));
    }

    if (!balances.pageInfo.hasNextPage) return [];
    const next = balances.pageInfo.endCursor;
    if (!next || next === after) {
      throw new Error(`Unable to load all ${network} balances`);
    }
    after = next;
  } while (after);

  return [];
};

export const configureRelayUtxoFetcher = (): void => {
  configureUtxoFetcher(fetchUtxos);
};
